import { z } from "zod";
/**
 * MVP contract ledger model. Separate from the legacy `types.ts`, which
 * describes the Slack-driven spreadsheet workflow (docs/existing-workflow.md).
 */
/** Legal entity that signed on our side. */
export const CONTRACTING_ENTITIES = ["JP", "UK", "US", "UNKNOWN"];
/** Progress of the upload → extraction pipeline. */
export const INGEST_STATUSES = [
    "UPLOADED",
    "EXTRACTING",
    "EXTRACTED",
    "APPROVED",
    "FAILED",
];
/**
 * State of the contract itself. Kept independent of `endDate` because an
 * auto-renewal clause means expiry cannot be derived by date arithmetic alone.
 * The MVP leaves this at UNKNOWN.
 */
export const LIFECYCLE_STATUSES = [
    "UNKNOWN",
    "ACTIVE",
    "EXPIRED",
    "TERMINATED",
];
/**
 * Who may see the record. The MVP only issues ORG (all legal staff), but the
 * field exists from the start so need-to-know can be added later without
 * migrating every row.
 */
export const VISIBILITIES = ["ORG", "RESTRICTED"];
/**
 * Rough classification, mirroring the `Contract Type` column of the legal
 * team's tracker. The codes are ours; the tracker's own labels ("Service
 * Agmt") are a display concern.
 *
 * Legal's note on that column is that the contract's name usually gives the
 * type away and they are not sure the column earns its keep. It is cheap to
 * fill from a document we are already reading, so it is filled — but nothing
 * downstream should depend on it being right, and `OTHER` is a real answer
 * rather than a failure.
 */
export const CONTRACT_TYPES = [
    "NDA",
    "SERVICE",
    "CONSULTANT",
    "REFERRAL",
    "SUPPLEMENTAL",
    "OTHER",
];
export const PDF_CONTENT_TYPE = "application/pdf";
/**
 * Why extraction failed. The reasons are kept apart so the page cap can be
 * revisited from measured data instead of guesswork: `PAGE_LIMIT_EXCEEDED`
 * counts the documents a higher cap would have saved
 * (docs/pdf-extraction-design.md §6).
 *
 * `INTERNAL_ERROR` is the catch-all for infrastructure and bugs. Folding those
 * into `GEMINI_REQUEST_FAILED` would make the Gemini failure rate read higher
 * than it is, which is the opposite of what these codes exist for.
 */
export const EXTRACTION_FAILURE_CODES = [
    "PAGE_LIMIT_EXCEEDED",
    "PDF_PARSE_FAILED",
    "GEMINI_REQUEST_FAILED",
    "PAGE_COVERAGE_INSUFFICIENT",
    "SCHEMA_VALIDATION_FAILED",
    "INTERNAL_ERROR",
];
const CalendarDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "must be YYYY-MM-DD")
    .refine((value) => {
    // The regex alone accepts impossible dates such as 2025-02-30. Date
    // parsing normalises those, so compare the round-tripped value.
    const parsed = new Date(`${value}T00:00:00Z`);
    return (!Number.isNaN(parsed.getTime()) &&
        parsed.toISOString().slice(0, 10) === value);
}, "must be a real calendar date");
const TimestampSchema = z.string().datetime();
export const AmountSchema = z.object({
    value: z.number().nonnegative(),
    currency: z.string().regex(/^[A-Z]{3}$/, "must be an ISO 4217 code"),
});
/**
 * Fields read out of the PDF. Optional values are nullable rather than
 * omittable: "the contract does not state an end date" must be
 * distinguishable from "extraction did not find one".
 */
export const ExtractedFieldsSchema = z.object({
    title: z.string().min(1),
    contractType: z.enum(CONTRACT_TYPES),
    counterparty: z.string().min(1),
    /**
     * The abbreviation the contract itself defines — "Acme Corporation
     * (hereinafter "Acme")". Null when it defines none: an abbreviation we
     * invented would not match what the contract is filed under.
     */
    counterpartyShortName: z.string().min(1).nullable(),
    contractingEntity: z.enum(CONTRACTING_ENTITIES),
    effectiveDate: CalendarDateSchema.nullable(),
    endDate: CalendarDateSchema.nullable(),
    autoRenewal: z.boolean().nullable(),
    /** Length of one renewal term, in months. The tracker's `Renewal Period`. */
    renewalTermMonths: z.number().int().positive().nullable(),
    /**
     * How far before expiry notice has to be given, in days, as the contract
     * states it. Kept alongside the derived date because the clause is the
     * authority: if `endDate` is later corrected, the deadline can be
     * recomputed, which a stored date alone would not allow.
     */
    renewalNoticePeriodDays: z.number().int().positive().nullable(),
    /**
     * By when someone has to act — `endDate` minus the notice period. Derived,
     * never asked of the model (see `deriveRenewalNoticeDeadline`), and stored
     * rather than computed on read so the deferred reminder feature can query a
     * date range directly (docs/mvp-design.md §7).
     */
    renewalNoticeDeadline: CalendarDateSchema.nullable(),
    amount: AmountSchema.nullable(),
});
/**
 * What the model is asked for.
 *
 * `renewalNoticeDeadline` is left out because it is derived from two other
 * fields: asking for it would invite an answer that disagrees with them, and
 * nothing could then say which of the two is right.
 */
export const ModelExtractedFieldsSchema = ExtractedFieldsSchema.omit({
    renewalNoticeDeadline: true,
});
/**
 * The same fields as read back out of the ledger.
 *
 * The fields added for the tracker default to null here, so a record written
 * before they existed still parses. The strict schema above is what the
 * extractor validates the model against: there, a missing key is a model that
 * ignored the response schema, and it has to fail rather than quietly become
 * "the contract does not say".
 */
export const StoredExtractedFieldsSchema = ExtractedFieldsSchema.extend({
    contractType: z.enum(CONTRACT_TYPES).default("OTHER"),
    counterpartyShortName: z.string().min(1).nullable().default(null),
    renewalTermMonths: z.number().int().positive().nullable().default(null),
    renewalNoticePeriodDays: z.number().int().positive().nullable().default(null),
    renewalNoticeDeadline: CalendarDateSchema.nullable().default(null),
});
/**
 * Page coverage of the PDF -> Markdown stage. Recorded on success too: a run
 * can be accepted while still having lost a page, and that has to stay
 * traceable afterwards.
 */
export const PageCoverageSchema = z.object({
    expectedPageCount: z.number().int().nonnegative(),
    detectedPageCount: z.number().int().nonnegative(),
    coverageRate: z.number().min(0).max(1),
    missingPageRanges: z.array(z.string()),
});
export const ContractRecordSchema = z.object({
    contractId: z.string().min(1),
    s3Bucket: z.string().min(1),
    s3Key: z.string().min(1),
    originalFileName: z.string().min(1),
    contentType: z.literal(PDF_CONTENT_TYPE),
    byteSize: z.number().int().nonnegative(),
    /** Null until the PDF has been opened. */
    pageCount: z.number().int().positive().nullable(),
    uploadedBy: z.string().email(),
    uploadedAt: TimestampSchema,
    updatedAt: TimestampSchema,
    ingestStatus: z.enum(INGEST_STATUSES),
    lifecycleStatus: z.enum(LIFECYCLE_STATUSES),
    visibility: z.enum(VISIBILITIES),
    /** Absent until extraction succeeds. */
    extracted: StoredExtractedFieldsSchema.optional(),
    extractionModel: z.string().optional(),
    extractedAt: TimestampSchema.optional(),
    /**
     * Raw model output, kept so a later prompt change can be re-evaluated
     * against past documents without re-processing every PDF.
     */
    extractionRaw: z.string().optional(),
    /** Set together with `ingestStatus = FAILED`. */
    failureCode: z.enum(EXTRACTION_FAILURE_CODES).optional(),
    failureReason: z.string().optional(),
    /** S3 key of the intermediate Markdown, kept beside the PDF. */
    extractedMarkdownKey: z.string().optional(),
    pageCoverage: PageCoverageSchema.optional(),
    /**
     * Who accepted the extracted fields, and when.
     *
     * Set together with `ingestStatus = APPROVED`: the record of a person
     * having taken responsibility for what a model read, which is what an
     * auditor asks for. Optional for the same reason `folderId` is — rows
     * written before approval existed carry neither, and `fromItem` throws on a
     * record that does not match the schema.
     *
     * The model's own answer stays in `extractionRaw`, so what the approver
     * changed remains recoverable.
     */
    approvedBy: z.string().email().optional(),
    approvedAt: TimestampSchema.optional(),
    managementNumber: z.string().optional(),
    /**
     * The folder this contract is filed in (`organisation.ts`).
     *
     * Optional, not nullable: every row written before folders existed has no
     * such attribute, and `fromItem` throws on a record that does not match the
     * schema — a required field here would make the whole ledger unreadable.
     * Absent means unfiled, which the folder screens show as its own bucket.
     */
    folderId: z.string().min(1).optional(),
});
/** Placeholder extraction with every stated field explicitly null. */
export function emptyExtractedFields() {
    return {
        title: "Untitled",
        contractType: "OTHER",
        counterparty: "Unknown",
        counterpartyShortName: null,
        contractingEntity: "UNKNOWN",
        effectiveDate: null,
        endDate: null,
        autoRenewal: null,
        renewalTermMonths: null,
        renewalNoticePeriodDays: null,
        renewalNoticeDeadline: null,
        amount: null,
    };
}
const INGEST_TRANSITIONS = {
    UPLOADED: ["EXTRACTING", "FAILED"],
    EXTRACTING: ["EXTRACTED", "FAILED"],
    EXTRACTED: ["APPROVED"],
    // Retryable: a transient Gemini or S3 error must not strand the record.
    FAILED: ["EXTRACTING"],
    APPROVED: [],
};
export function canTransitionIngest(from, to) {
    return INGEST_TRANSITIONS[from].includes(to);
}
/**
 * When notice has to be given: `endDate` minus the notice period.
 *
 * Derived here rather than asked of the model. Date arithmetic is the one part
 * of this record a language model has no business doing — it is exactly
 * reproducible, and a plausible-looking wrong date is indistinguishable from a
 * right one once it is in the ledger.
 *
 * Either input missing yields null. A contract with no end date has no
 * deadline to compute, and answering with today's date, or with the end date
 * itself, would put a wrong entry in front of whoever is chasing renewals.
 */
export function deriveRenewalNoticeDeadline(endDate, noticePeriodDays) {
    if (endDate === null || noticePeriodDays === null) {
        return null;
    }
    // UTC throughout: these are calendar dates, not instants. Local-time
    // arithmetic would shift the answer by a day either side of a DST boundary.
    const end = new Date(`${endDate}T00:00:00Z`);
    if (Number.isNaN(end.getTime())) {
        return null;
    }
    end.setUTCDate(end.getUTCDate() - noticePeriodDays);
    return end.toISOString().slice(0, 10);
}
//# sourceMappingURL=contract.js.map